import * as React from 'react';

import { compose } from 'recompose';

import Retracked from 'js/app/retracked';

import { Grid } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import PersonalizedEmbeddedMsg from 'bundles/degree-description/components/modules/personalization/PersonalizedEmbeddedMsg';
import { getCtaActionXddp, getMessagesToDisplay } from 'bundles/degree-description/utils/personalizedMessageCriteria';
import MobileLexUpsell from 'bundles/xddp/components/academics/MobileLexUpsell';
import ProgramSpecializations from 'bundles/xddp/components/academics/ProgramSpecializations';
import AcademicsPageSchemaMarkup from 'bundles/xddp/components/seo/AcademicsPageSchemaMarkup';
import Container from 'bundles/xddp/components/shared/Container';
import InfoSidebar from 'bundles/xddp/components/shared/InfoSidebar';
import { useScrollToStickyBar } from 'bundles/xddp/hooks/useScrollToStickyBar';
import type { DegreeSubPageProps } from 'bundles/xddp/types';

import 'css!bundles/xddp/components/__styles__/AcademicsPage';

const AcademicsPage: React.FC<DegreeSubPageProps> = ({
  pageContent,
  userAgent,
  personalizedDdpData,
  handlePersonalizedRequestInfoClick,
  isStickyBarPresent,
}) => {
  const {
    curriculum,
    programLength,
    programFlexibility,
    programSpecializationsCollection,
    mobileLexUpsell,
    atAGlance,
    programName,
    partnerInformation,
    expressionOfInterestContainer,
    slug,
  } = pageContent;

  const messagesToDisplay = getMessagesToDisplay(
    personalizedDdpData,
    slug,
    programName,
    partnerInformation?.partnerName
  );
  const embeddedAction = getCtaActionXddp(
    messagesToDisplay?.embeddedMessageToDisplay?.ctaActionType,
    expressionOfInterestContainer?.applyButtonUrl ?? expressionOfInterestContainer?.hostedApplicationUrl,
    handlePersonalizedRequestInfoClick
  );

  useScrollToStickyBar(isStickyBarPresent);

  return (
    <div className="rc-AcademicsPage">
      <AcademicsPageSchemaMarkup pageContent={pageContent} />
      <Container>
        <Grid container>
          <Grid item md={8} sm={12}>
            <Markdown className="curriculum rc-Section" source={curriculum} />
            <ProgramSpecializations specializations={programSpecializationsCollection?.items} />
            {messagesToDisplay.embeddedMessageToDisplay && (
              <PersonalizedEmbeddedMsg
                personalizedMessage={messagesToDisplay.embeddedMessageToDisplay}
                ctaAction={embeddedAction}
                onXddp
              />
            )}
            <Markdown className="programLength" source={programLength} />
            <Markdown className="programFlexibility rc-Section" source={programFlexibility} />
            <MobileLexUpsell userAgent={userAgent} {...mobileLexUpsell} />
          </Grid>
          <InfoSidebar userAgent={userAgent} atAGlance={atAGlance} />
        </Grid>
      </Container>
    </div>
  );
};

export default compose<DegreeSubPageProps, DegreeSubPageProps>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'xddp',
      page: 'academics_page',
    },
  }))
)(AcademicsPage);
